/**
 * Storage utilities for configuration persistence
 */

const STORAGE_KEYS = {
  CONFIGS: 'fokusgrupp_saved_configs',
  PERSONAS: 'fokusgrupp_saved_personas',
  SESSION_RECOVERY: 'fokusgrupp_session_recovery',
  PREFERENCES: 'fokusgrupp_preferences',
  DEMOGRAPHIC_PRESETS: 'fokusgrupp_custom_demographics',
};

// Configuration Management
export function saveConfiguration(name, config, selectedModels, personas = null) {
  const configs = getSavedConfigurations();
  const id = `config_${Date.now()}`;

  const configData = {
    id,
    name,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    config,
    selectedModels,
    personas,
  };

  configs[id] = configData;
  localStorage.setItem(STORAGE_KEYS.CONFIGS, JSON.stringify(configs));
  return id;
}

export function getSavedConfigurations() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.CONFIGS);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.error('Error loading configurations:', e);
    return {};
  }
}

export function loadConfiguration(id) {
  const configs = getSavedConfigurations();
  return configs[id] || null;
}

export function deleteConfiguration(id) {
  const configs = getSavedConfigurations();
  delete configs[id];
  localStorage.setItem(STORAGE_KEYS.CONFIGS, JSON.stringify(configs));
}

export function updateConfiguration(id, updates) {
  const configs = getSavedConfigurations();
  if (configs[id]) {
    configs[id] = {
      ...configs[id],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEYS.CONFIGS, JSON.stringify(configs));
  }
}

// Export/Import as JSON
export function exportConfigurationToJSON(id) {
  const config = loadConfiguration(id);
  if (!config) return null;

  const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `fokusgrupp_${config.name.replace(/\s+/g, '_')}_${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportAllConfigurationsToJSON() {
  const configs = getSavedConfigurations();
  const blob = new Blob([JSON.stringify(configs, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `fokusgrupp_all_configs_${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function importConfigurationFromJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);

        // Check if it's a single config or multiple
        if (data.id && data.config) {
          // Single config
          const configs = getSavedConfigurations();
          const newId = `config_${Date.now()}`;
          configs[newId] = { ...data, id: newId, importedAt: new Date().toISOString() };
          localStorage.setItem(STORAGE_KEYS.CONFIGS, JSON.stringify(configs));
          resolve({ imported: 1, ids: [newId] });
        } else {
          // Multiple configs
          const configs = getSavedConfigurations();
          const ids = [];
          Object.values(data).forEach((cfg, idx) => {
            if (cfg.config) {
              const newId = `config_${Date.now()}_${idx}`;
              configs[newId] = { ...cfg, id: newId, importedAt: new Date().toISOString() };
              ids.push(newId);
            }
          });
          localStorage.setItem(STORAGE_KEYS.CONFIGS, JSON.stringify(configs));
          resolve({ imported: ids.length, ids });
        }
      } catch (e) {
        reject(new Error('Invalid configuration file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

// Persona Templates
export function savePersonaTemplate(name, personas) {
  const templates = getPersonaTemplates();
  const id = `persona_${Date.now()}`;

  templates[id] = {
    id,
    name,
    createdAt: new Date().toISOString(),
    personas,
  };

  localStorage.setItem(STORAGE_KEYS.PERSONAS, JSON.stringify(templates));
  return id;
}

export function getPersonaTemplates() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PERSONAS);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.error('Error loading persona templates:', e);
    return {};
  }
}

export function loadPersonaTemplate(id) {
  const templates = getPersonaTemplates();
  return templates[id]?.personas || null;
}

export function deletePersonaTemplate(id) {
  const templates = getPersonaTemplates();
  delete templates[id];
  localStorage.setItem(STORAGE_KEYS.PERSONAS, JSON.stringify(templates));
}

// Session Recovery
export function saveSessionRecovery(sessionData) {
  const recoveryData = {
    ...sessionData,
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEYS.SESSION_RECOVERY, JSON.stringify(recoveryData));
}

export function getSessionRecovery() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SESSION_RECOVERY);
    if (!data) return null;

    const recovery = JSON.parse(data);
    // Only return if saved within last 24 hours
    const savedAt = new Date(recovery.savedAt);
    const hoursSinceSave = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);

    if (hoursSinceSave > 24) {
      clearSessionRecovery();
      return null;
    }

    return recovery;
  } catch (e) {
    console.error('Error loading session recovery:', e);
    return null;
  }
}

export function clearSessionRecovery() {
  localStorage.removeItem(STORAGE_KEYS.SESSION_RECOVERY);
}

// Custom Demographic Presets
export function saveCustomDemographic(name, description, settings) {
  const presets = getCustomDemographics();
  const id = `demo_${Date.now()}`;

  presets[id] = {
    id,
    name,
    description,
    settings,
    createdAt: new Date().toISOString(),
  };

  localStorage.setItem(STORAGE_KEYS.DEMOGRAPHIC_PRESETS, JSON.stringify(presets));
  return id;
}

export function getCustomDemographics() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.DEMOGRAPHIC_PRESETS);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.error('Error loading custom demographics:', e);
    return {};
  }
}

export function deleteCustomDemographic(id) {
  const presets = getCustomDemographics();
  delete presets[id];
  localStorage.setItem(STORAGE_KEYS.DEMOGRAPHIC_PRESETS, JSON.stringify(presets));
}

// User Preferences
export function savePreferences(prefs) {
  const current = getPreferences();
  const updated = { ...current, ...prefs };
  localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(updated));
}

export function getPreferences() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
    return data ? JSON.parse(data) : {
      language: 'sv',
      theme: 'light',
      defaultModeratorModel: 'claude-sonnet-4.6',
      defaultRounds: 5,
      defaultParticipants: 5,
    };
  } catch (e) {
    return {};
  }
}
